import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  subMonths,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  Filter,
  List,
  MapPin,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  worship: { label: 'Worship', color: 'bg-violet-600', soft: 'bg-violet-50 text-violet-700' },
  kinderwerk: { label: 'Kinderwerk', color: 'bg-amber-500', soft: 'bg-amber-50 text-amber-700' },
  team_slots: { label: 'Teams', color: 'bg-sky-600', soft: 'bg-sky-50 text-sky-700' },
  bookings: { label: 'Reserveringen', color: 'bg-teal-600', soft: 'bg-teal-50 text-teal-700' },
  connectgroepen: { label: 'Connectgroepen', color: 'bg-emerald-600', soft: 'bg-emerald-50 text-emerald-700' },
  absences: { label: 'Afwezigheid', color: 'bg-rose-500', soft: 'bg-rose-50 text-rose-700' },
};

const sourceMeta = (source) => SOURCE_META[source] || {
  label: source || 'Overig', color: 'bg-slate-600', soft: 'bg-slate-100 text-slate-700',
};

const personKey = (person) => {
  if (person.user_id) return `user:${person.user_id}`;
  if (person.member_id) return `member:${person.member_id}`;
  return `relation:${person.relation_id}`;
};

const statusLabel = (status) => ({
  confirmed: 'Bevestigd', pending: 'In afwachting', cancelled: 'Geannuleerd',
  gepland: 'Gepland', concept: 'Concept', afwezig: 'Afwezig', invited: 'Uitgenodigd',
  uitgenodigd: 'Uitgenodigd', completed: 'Afgerond', afgerond: 'Afgerond',
}[status] || status || 'Onbekend');

const EMPTY_ASSIGNMENT = { title: '', slot_date: '', start_time: '09:00', end_time: '10:00', role: '', person: '', location: '', notes: '' };
const EMPTY_ABSENCE = { start_date: '', end_date: '', person: '', reason: '' };
const EMPTY_WORKLOAD_PREFERENCE = { max_assignments_per_month: '', consecutive_weeks_warning: '4' };

const WorkloadPersonRow = ({ person, maximum, canManage, onEdit }) => {
  const moduleAssignments = Object.entries(person.assignments_by_module || {});
  const warnings = person.warnings || [];
  return (
    <div className="p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><h4 className="font-semibold text-slate-900">{person.display_name}</h4><span className="text-xs text-slate-400">{person.person_type}</span></div>
          <div className="mt-2 h-2 max-w-xl overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.max(4, (person.total_assignments / maximum) * 100)}%` }} /></div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500"><span>{person.total_assignments} inzetten</span><span>{person.active_weeks} actieve weken</span><span>langste reeks: {person.longest_consecutive_weeks}</span>{person.max_assignments_per_month != null && <span>maximum: {person.max_assignments_per_month}</span>}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">{moduleAssignments.map(([module, count]) => <span key={module} className={`rounded-full px-2 py-1 text-xs font-medium ${sourceMeta(module).soft}`}>{sourceMeta(module).label} {count}</span>)}</div>
          {!!warnings.length && <div className="mt-3 space-y-1">{warnings.map((warning) => <p key={warning.warning_type} className="flex items-center gap-2 text-sm font-medium text-amber-700"><AlertTriangle className="h-4 w-4" />{warning.message}</p>)}</div>}
        </div>
        {canManage && <Button variant="outline" className="rounded-xl" onClick={() => onEdit(person)}><Settings2 className="mr-2 h-4 w-4" /> Grenzen</Button>}
      </div>
    </div>
  );
};

const CentralPlanningPage = () => {
  const navigate = useNavigate();
  const { hasCapability } = useAuth();
  const canManage = hasCapability('central_planning_manage');
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [view, setView] = useState('month');
  const [entries, setEntries] = useState([]);
  const [providerStatuses, setProviderStatuses] = useState([]);
  const [workload, setWorkload] = useState({ people: [], assignments_by_module: {}, total_assignments: 0 });
  const [complete, setComplete] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [people, setPeople] = useState([]);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState(EMPTY_ASSIGNMENT);
  const [absenceForm, setAbsenceForm] = useState(EMPTY_ABSENCE);
  const [workloadDialogOpen, setWorkloadDialogOpen] = useState(false);
  const [workloadPerson, setWorkloadPerson] = useState(null);
  const [workloadPreference, setWorkloadPreference] = useState(EMPTY_WORKLOAD_PREFERENCE);
  const [saving, setSaving] = useState(false);

  const loadPlanning = useCallback(async () => {
    setLoading(true);
    try {
      const dateFrom = startOfMonth(currentMonth);
      const dateTo = addMonths(dateFrom, 1);
      const params = { date_from: dateFrom.toISOString(), date_to: dateTo.toISOString() };
      const [planningResponse, workloadResponse] = await Promise.all([
        axios.get(`${API}/central-planning`, { params }),
        axios.get(`${API}/central-planning/workload`, { params }),
      ]);
      setEntries(planningResponse.data.entries || []);
      setProviderStatuses(planningResponse.data.provider_statuses || []);
      setComplete(planningResponse.data.complete !== false && workloadResponse.data.complete !== false);
      setWorkload(workloadResponse.data);
    } catch (error) {
      setEntries([]);
      setProviderStatuses([]);
      setWorkload({ people: [], assignments_by_module: {}, total_assignments: 0 });
      setComplete(false);
      toast.error(error.response?.data?.detail || 'Kon Centrale Planning niet laden');
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { loadPlanning(); }, [loadPlanning]);

  useEffect(() => {
    if (!canManage) return;
    axios.get(`${API}/central-planning/people`)
      .then((response) => setPeople(response.data || []))
      .catch(() => toast.error('Kon personen voor planning niet laden'));
  }, [canManage]);

  const sourceOptions = useMemo(() => [...new Set(entries.map((entry) => entry.source_module))].sort(), [entries]);
  const statusOptions = useMemo(() => [...new Set(entries.map((entry) => entry.status).filter(Boolean))].sort(), [entries]);
  const personOptions = useMemo(() => {
    const people = new Map();
    entries.forEach((entry) => (entry.assignees || []).forEach((person) => people.set(personKey(person), person.display_name)));
    return [...people.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [entries]);

  const filteredEntries = useMemo(() => entries.filter((entry) => {
    if (sourceFilter !== 'all' && entry.source_module !== sourceFilter) return false;
    if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
    if (personFilter !== 'all' && !(entry.assignees || []).some((person) => personKey(person) === personFilter)) return false;
    return true;
  }), [entries, personFilter, sourceFilter, statusFilter]);

  const calendarDays = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 }),
  }), [currentMonth]);

  const entriesByDay = useMemo(() => {
    const result = new Map();
    filteredEntries.forEach((entry) => {
      const start = new Date(entry.start_at);
      const endValue = entry.end_at ? new Date(entry.end_at) : start;
      const end = entry.end_at ? new Date(endValue.getTime() - 1) : endValue;
      eachDayOfInterval({ start, end }).forEach((day) => {
        const key = format(day, 'yyyy-MM-dd');
        if (!result.has(key)) result.set(key, []);
        result.get(key).push(entry);
      });
    });
    return result;
  }, [filteredEntries]);

  const failedProviders = providerStatuses.filter((provider) => provider.status === 'failed');
  const resetFilters = () => {
    setSourceFilter('all');
    setStatusFilter('all');
    setPersonFilter('all');
  };

  const openSource = (entry) => {
    if (entry.detail_path) navigate(entry.detail_path);
  };

  const personPayload = (value) => {
    const [personType, personId] = value.split(':');
    return { [`${personType}_id`]: personId };
  };

  const openNewAssignment = () => {
    setEditingAssignment(null);
    setAssignmentForm({ ...EMPTY_ASSIGNMENT, slot_date: format(new Date(), 'yyyy-MM-dd') });
    setAssignmentDialogOpen(true);
  };

  const openEditAssignment = (entry) => {
    const assignee = entry.assignees?.[0];
    setEditingAssignment(entry);
    setAssignmentForm({
      title: entry.title || '', slot_date: format(new Date(entry.start_at), 'yyyy-MM-dd'),
      start_time: format(new Date(entry.start_at), 'HH:mm'), end_time: format(new Date(entry.end_at), 'HH:mm'),
      role: assignee?.role || '', person: assignee ? personKey(assignee) : '',
      location: entry.location || '', notes: entry.description || '',
    });
    setAssignmentDialogOpen(true);
  };

  const saveAssignment = async (event, override = null) => {
    event?.preventDefault();
    if (!assignmentForm.title || !assignmentForm.slot_date || !assignmentForm.person) {
      toast.error('Vul titel, datum en persoon in');
      return;
    }
    const payload = {
      ...assignmentForm,
      person: undefined,
      ...personPayload(assignmentForm.person),
      allow_conflict: Boolean(override),
      override_reason: override || null,
    };
    setSaving(true);
    try {
      if (editingAssignment) await axios.put(`${API}/central-planning/assignments/${editingAssignment.source_id}`, payload);
      else await axios.post(`${API}/central-planning/assignments`, payload);
      toast.success(editingAssignment ? 'Inzet bijgewerkt' : 'Inzet toegevoegd');
      setAssignmentDialogOpen(false);
      await loadPlanning();
    } catch (error) {
      if (error.response?.status === 409) {
        const reason = window.prompt('Deze persoon heeft overlappende planning. Geef een reden om toch in te plannen:');
        if (reason?.trim()) {
          setSaving(false);
          await saveAssignment(null, reason.trim());
          return;
        }
      } else {
        toast.error(error.response?.data?.detail || 'Kon inzet niet opslaan');
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAbsence = async (event) => {
    event.preventDefault();
    if (!absenceForm.start_date || !absenceForm.end_date || !absenceForm.person) {
      toast.error('Vul persoon en datums in');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/central-planning/absences`, {
        start_date: absenceForm.start_date, end_date: absenceForm.end_date, reason: absenceForm.reason,
        ...personPayload(absenceForm.person),
      });
      toast.success('Afwezigheid toegevoegd');
      setAbsenceDialogOpen(false);
      await loadPlanning();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon afwezigheid niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const deleteCentralEntry = async (entry) => {
    if (!window.confirm(`Weet je zeker dat je “${entry.title}” wilt verwijderen?`)) return;
    const endpoint = entry.source_module === 'absences'
      ? `${API}/central-planning/absences/${entry.source_id}`
      : `${API}/central-planning/assignments/${entry.source_id}`;
    try {
      await axios.delete(endpoint);
      toast.success('Planning verwijderd');
      await loadPlanning();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon planning niet verwijderen');
    }
  };

  const openWorkloadPreference = (person) => {
    setWorkloadPerson(person);
    setWorkloadPreference({
      max_assignments_per_month: person.max_assignments_per_month ?? '',
      consecutive_weeks_warning: String(person.consecutive_weeks_warning || 4),
    });
    setWorkloadDialogOpen(true);
  };

  const saveWorkloadPreference = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/central-planning/workload/preferences/${workloadPerson.person_type}/${workloadPerson.person_id}`, {
        max_assignments_per_month: workloadPreference.max_assignments_per_month === '' ? null : Number(workloadPreference.max_assignments_per_month),
        consecutive_weeks_warning: Number(workloadPreference.consecutive_weeks_warning),
      });
      toast.success('Belastingsgrenzen bijgewerkt');
      setWorkloadDialogOpen(false);
      await loadPlanning();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon belastingsgrenzen niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const workloadPeople = workload.people || [];
  const workloadModules = Object.entries(workload.assignments_by_module || {}).sort((a, b) => b[1] - a[1]);
  const workloadMaximum = Math.max(1, ...workloadPeople.map((person) => person.total_assignments));
  const workloadWarningCount = workloadPeople.reduce((sum, person) => sum + person.warnings.length, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="central-planning-page">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-teal-700">
              <CalendarDays className="h-4 w-4" /> Organisatiebreed overzicht
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Centrale Planning</h1>
            <p className="mt-1 text-slate-500">Decentraal plannen, centraal zien en controleren.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canManage && <>
              <Button variant="outline" className="rounded-xl" onClick={() => { setAbsenceForm({ ...EMPTY_ABSENCE, start_date: format(new Date(), 'yyyy-MM-dd'), end_date: format(new Date(), 'yyyy-MM-dd') }); setAbsenceDialogOpen(true); }}>
                Afwezigheid
              </Button>
              <Button className="rounded-xl bg-teal-600 text-white hover:bg-teal-700" onClick={openNewAssignment}>
                <Plus className="mr-2 h-4 w-4" /> Nieuwe inzet
              </Button>
            </>}
            <div className="flex rounded-xl bg-slate-100 p-1">
              <button onClick={() => setView('month')} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${view === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                <CalendarDays className="h-4 w-4" /> Maand
              </button>
              <button onClick={() => setView('list')} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                <List className="h-4 w-4" /> Lijst
              </button>
              <button onClick={() => setView('workload')} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${view === 'workload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                <BarChart3 className="h-4 w-4" /> Inzet
              </button>
            </div>
            <Button variant="outline" size="icon" className="rounded-xl" onClick={loadPlanning} title="Vernieuwen">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {!complete && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900" role="alert">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Het overzicht is mogelijk onvolledig</p>
              <p className="text-sm text-amber-800">
                {failedProviders.length ? `${failedProviders.map((item) => sourceMeta(item.source_module).label).join(', ')} kon niet worden geladen.` : 'Niet alle planningsbronnen konden worden gecontroleerd.'}
              </p>
            </div>
          </div>
        )}

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Filter className="h-4 w-4" /> Filters</div>
              <div className="grid flex-1 gap-3 sm:grid-cols-3">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Alle modules" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Alle modules</SelectItem>{sourceOptions.map((source) => <SelectItem value={source} key={source}>{sourceMeta(source).label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Alle statussen</SelectItem>{statusOptions.map((status) => <SelectItem value={status} key={status}>{statusLabel(status)}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={personFilter} onValueChange={setPersonFilter}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Alle personen" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Alle personen</SelectItem>{personOptions.map(([key, name]) => <SelectItem value={key} key={key}>{name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(sourceFilter !== 'all' || statusFilter !== 'all' || personFilter !== 'all') && <Button variant="ghost" onClick={resetFilters}>Wis filters</Button>}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="rounded-xl" onClick={() => setCurrentMonth((month) => subMonths(month, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setCurrentMonth(startOfMonth(new Date()))}>Vandaag</Button>
            <Button variant="outline" size="icon" className="rounded-xl" onClick={() => setCurrentMonth((month) => addMonths(month, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <h2 className="text-lg font-semibold capitalize text-slate-900">{format(currentMonth, 'MMMM yyyy', { locale: nl })}</h2>
          <span className="text-sm text-slate-500">{filteredEntries.length} items</span>
        </div>

        {loading ? (
          <div className="grid animate-pulse grid-cols-2 gap-3 md:grid-cols-4"><div className="h-40 rounded-2xl bg-slate-200" /><div className="h-40 rounded-2xl bg-slate-200" /><div className="h-40 rounded-2xl bg-slate-200" /><div className="h-40 rounded-2xl bg-slate-200" /></div>
        ) : view === 'month' ? (
          <Card className="overflow-hidden border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  <div className="grid grid-cols-7 bg-slate-50">{['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day) => <div key={day} className="border-b p-3 text-center text-xs font-semibold uppercase text-slate-500">{day}</div>)}</div>
                  <div className="grid grid-cols-7">
                {calendarDays.map((day) => {
                  const dayEntries = entriesByDay.get(format(day, 'yyyy-MM-dd')) || [];
                  const today = isSameDay(day, new Date());
                  return (
                    <div key={day.toISOString()} className={`min-h-28 border-b border-r border-slate-100 p-2 ${isSameMonth(day, currentMonth) ? 'bg-white' : 'bg-slate-50/70 text-slate-400'}`}>
                      <div className={`mb-2 flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium ${today ? 'bg-teal-600 text-white' : ''}`}>{format(day, 'd')}</div>
                      <div className="space-y-1">
                        {dayEntries.slice(0, 3).map((entry) => (
                          <button key={entry.id} onClick={() => entry.source_module === 'team_slots' && entry.source_type === 'algemeen' && canManage ? openEditAssignment(entry) : openSource(entry)} disabled={!entry.detail_path && !(entry.source_module === 'team_slots' && entry.source_type === 'algemeen' && canManage)} className={`block w-full truncate rounded-md px-2 py-1 text-left text-[11px] font-medium text-white ${sourceMeta(entry.source_module).color} ${entry.detail_path || canManage ? 'hover:opacity-90' : 'cursor-default'}`} title={entry.title}>
                            {entry.conflict_count > 0 && '⚠ '}{entry.all_day ? '' : `${format(new Date(entry.start_at), 'HH:mm')} `}{entry.title}
                          </button>
                        ))}
                        {dayEntries.length > 3 && <p className="px-1 text-xs text-slate-500">+{dayEntries.length - 3} meer</p>}
                      </div>
                    </div>
                  );
                })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : view === 'list' ? (
          <div className="space-y-3">
            {filteredEntries.map((entry) => {
              const meta = sourceMeta(entry.source_module);
              return (
                <Card key={entry.id} className="border-0 shadow-sm">
                  <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center">
                    <div className="w-20 shrink-0 text-center md:border-r md:border-slate-100">
                      <p className="text-xs font-semibold uppercase text-slate-500">{format(new Date(entry.start_at), 'EEE', { locale: nl })}</p>
                      <p className="text-2xl font-bold text-slate-900">{format(new Date(entry.start_at), 'd')}</p>
                      <p className="text-xs text-slate-500">{format(new Date(entry.start_at), 'MMM', { locale: nl })}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${meta.soft}`}>{meta.label}</span>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{statusLabel(entry.status)}</span>
                        {entry.conflict_count > 0 && <span className="flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700"><AlertTriangle className="h-3 w-3" /> {entry.conflict_count} conflict{entry.conflict_count === 1 ? '' : 'en'}</span>}
                      </div>
                      <h3 className="truncate font-semibold text-slate-900">{entry.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5"><Clock3 className="h-4 w-4" />{entry.all_day ? 'Hele dag' : `${format(new Date(entry.start_at), 'HH:mm')}${entry.end_at ? `–${format(new Date(entry.end_at), 'HH:mm')}` : ''}`}</span>
                        {entry.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{entry.location}</span>}
                        {!!entry.assignees?.length && <span className="flex items-center gap-1.5"><UserRound className="h-4 w-4" />{entry.assignees.map((person) => person.display_name).join(', ')}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {entry.detail_path && <Button variant="outline" className="rounded-xl" onClick={() => openSource(entry)}>Open bron <ExternalLink className="ml-2 h-4 w-4" /></Button>}
                      {canManage && entry.source_module === 'team_slots' && entry.source_type === 'algemeen' && <Button variant="outline" className="rounded-xl" onClick={() => openEditAssignment(entry)}>Bewerken</Button>}
                      {canManage && (entry.source_module === 'absences' || (entry.source_module === 'team_slots' && entry.source_type === 'algemeen')) && <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteCentralEntry(entry)} title="Verwijderen"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {!filteredEntries.length && <Card className="border-0 shadow-sm"><CardContent className="py-16 text-center"><CalendarDays className="mx-auto mb-3 h-10 w-10 text-slate-300" /><h3 className="font-semibold text-slate-900">Geen planning gevonden</h3><p className="mt-1 text-sm text-slate-500">Pas de filters aan of kies een andere maand.</p></CardContent></Card>}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-3">
              <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Totale persoonsinzetten</p><p className="mt-1 text-3xl font-bold text-slate-900">{workload.total_assignments}</p></CardContent></Card>
              <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Ingezette personen</p><p className="mt-1 text-3xl font-bold text-slate-900">{workloadPeople.length}</p></CardContent></Card>
              <Card className="border-0 shadow-sm"><CardContent className="p-5"><p className="text-sm text-slate-500">Waarschuwingen</p><p className="mt-1 text-3xl font-bold text-amber-600">{workloadWarningCount}</p></CardContent></Card>
            </div>

            {!!workloadModules.length && <Card className="border-0 shadow-sm"><CardContent className="p-5"><h3 className="font-semibold text-slate-900">Verdeling per module</h3><div className="mt-4 flex flex-wrap gap-2">{workloadModules.map(([module, count]) => <span key={module} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${sourceMeta(module).soft}`}>{sourceMeta(module).label}: {count}</span>)}</div></CardContent></Card>}

            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <div className="border-b p-5"><h3 className="font-semibold text-slate-900">Inzet per persoon</h3><p className="mt-1 text-sm text-slate-500">Tellingen zijn transparant en wegen niet iedere taak automatisch even zwaar.</p></div>
                <div className="divide-y">
                  {workloadPeople.map((person) => <WorkloadPersonRow key={`${person.person_type}:${person.person_id}`} person={person} maximum={workloadMaximum} canManage={canManage} onEdit={openWorkloadPreference} />)}
                  {!workloadPeople.length && <div className="p-12 text-center text-slate-500">Geen persoonsinzetten gevonden in deze maand.</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editingAssignment ? 'Inzet bewerken' : 'Nieuwe algemene inzet'}</DialogTitle></DialogHeader>
            <form onSubmit={saveAssignment} className="space-y-4">
              <div><Label>Titel</Label><Input value={assignmentForm.title} onChange={(event) => setAssignmentForm({ ...assignmentForm, title: event.target.value })} placeholder="Bijvoorbeeld ontvangstteam" /></div>
              <div><Label>Persoon</Label><Select value={assignmentForm.person} onValueChange={(person) => setAssignmentForm({ ...assignmentForm, person })}><SelectTrigger><SelectValue placeholder="Kies gebruiker, lid of relatie" /></SelectTrigger><SelectContent>{people.map((person) => <SelectItem key={`${person.person_type}:${person.person_id}`} value={`${person.person_type}:${person.person_id}`}>{person.display_name} · {person.person_type}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><Label>Datum</Label><Input type="date" value={assignmentForm.slot_date} onChange={(event) => setAssignmentForm({ ...assignmentForm, slot_date: event.target.value })} /></div>
                <div><Label>Starttijd</Label><Input type="time" value={assignmentForm.start_time} onChange={(event) => setAssignmentForm({ ...assignmentForm, start_time: event.target.value })} /></div>
                <div><Label>Eindtijd</Label><Input type="time" value={assignmentForm.end_time} onChange={(event) => setAssignmentForm({ ...assignmentForm, end_time: event.target.value })} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Rol of taak</Label><Input value={assignmentForm.role} onChange={(event) => setAssignmentForm({ ...assignmentForm, role: event.target.value })} /></div>
                <div><Label>Locatie</Label><Input value={assignmentForm.location} onChange={(event) => setAssignmentForm({ ...assignmentForm, location: event.target.value })} /></div>
              </div>
              <div><Label>Notities</Label><Textarea value={assignmentForm.notes} onChange={(event) => setAssignmentForm({ ...assignmentForm, notes: event.target.value })} /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setAssignmentDialogOpen(false)}>Annuleren</Button><Button type="submit" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Afwezigheid toevoegen</DialogTitle></DialogHeader>
            <form onSubmit={saveAbsence} className="space-y-4">
              <div><Label>Persoon</Label><Select value={absenceForm.person} onValueChange={(person) => setAbsenceForm({ ...absenceForm, person })}><SelectTrigger><SelectValue placeholder="Kies gebruiker, lid of relatie" /></SelectTrigger><SelectContent>{people.map((person) => <SelectItem key={`${person.person_type}:${person.person_id}`} value={`${person.person_type}:${person.person_id}`}>{person.display_name} · {person.person_type}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Van</Label><Input type="date" value={absenceForm.start_date} onChange={(event) => setAbsenceForm({ ...absenceForm, start_date: event.target.value })} /></div>
                <div><Label>Tot en met</Label><Input type="date" value={absenceForm.end_date} onChange={(event) => setAbsenceForm({ ...absenceForm, end_date: event.target.value })} /></div>
              </div>
              <div><Label>Reden</Label><Textarea value={absenceForm.reason} onChange={(event) => setAbsenceForm({ ...absenceForm, reason: event.target.value })} placeholder="Optioneel" /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setAbsenceDialogOpen(false)}>Annuleren</Button><Button type="submit" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={workloadDialogOpen} onOpenChange={setWorkloadDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Belastingsgrenzen · {workloadPerson?.display_name}</DialogTitle></DialogHeader>
            <form onSubmit={saveWorkloadPreference} className="space-y-4">
              <div><Label>Maximum aantal inzetten per maand</Label><Input type="number" min="0" max="100" value={workloadPreference.max_assignments_per_month} onChange={(event) => setWorkloadPreference({ ...workloadPreference, max_assignments_per_month: event.target.value })} placeholder="Geen maximum" /><p className="mt-1 text-xs text-slate-500">Laat leeg wanneer geen persoonlijke bovengrens is afgesproken.</p></div>
              <div><Label>Waarschuwen vanaf opeenvolgende weken</Label><Input type="number" min="2" max="12" value={workloadPreference.consecutive_weeks_warning} onChange={(event) => setWorkloadPreference({ ...workloadPreference, consecutive_weeks_warning: event.target.value })} /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setWorkloadDialogOpen(false)}>Annuleren</Button><Button type="submit" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CentralPlanningPage;
